import React from "react";
import PartyCallMonthCalendarSheet from "./PartyCallMonthCalendarSheet";
import mainLogo from "../../../Image/Main_logo.png";
import { FaImage } from "react-icons/fa";
import "./PartyCallPrintReport.css";

const isJpgOrPng = (url) => {
  if (!url || typeof url !== "string") return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase().trim();
  return (
    clean.endsWith(".jpg") ||
    clean.endsWith(".jpeg") ||
    clean.endsWith(".png") ||
    url.startsWith("data:image/jpeg") ||
    url.startsWith("data:image/jpg") ||
    url.startsWith("data:image/png") ||
    (/\.(jpe?g|png)($|\?)/i).test(url)
  );
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS_OF_WEEK = [
  { name: "Sunday", isWeekend: true },
  { name: "Monday", isWeekend: false },
  { name: "Tuesday", isWeekend: false },
  { name: "Wednesday", isWeekend: false },
  { name: "Thursday", isWeekend: false },
  { name: "Friday", isWeekend: false },
  { name: "Saturday", isWeekend: false },
];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const calculateDuration = (startDate, endDate, status) => {
  if (status?.toLowerCase() !== "completed") return null;
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end) || end < start) return null;
  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

  const parts = [];
  if (diffDays > 0) parts.push(`${diffDays}d`);
  if (diffHrs > 0) parts.push(`${diffHrs}h`);
  if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
  return parts.join(" ");
};

const getStatusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completed") {
    return {
      className: "status-badge-completed",
      label: "Completed",
    };
  }
  if (s === "pending") {
    return {
      className: "status-badge-pending",
      label: "Pending",
    };
  }
  if (
    s.includes("accept") ||
    s.includes("solve") ||
    s.includes("review") ||
    s.includes("running")
  ) {
    return {
      className: "status-badge-review",
      label: status || "Under Review",
    };
  }
  return {
    className: "status-badge-default",
    label: status || "Open",
  };
};

const PartyCallPrintReport = ({
  party,
  month,
  year,
  projectId = "ALL",
  partyProjects = [],
  calendarWeeks = [],
  callsForMonth = [],
  monthStats = { total: 0, completed: 0, pending: 0, other: 0 },
  includeHistory = true,
  includeImages = true,
  isScreenPreview = false,
}) => {
  const partyName = party?.CompanyName || "All Parties";
  const partyType = party?.Type || "CLIENT";
  const monthName = MONTH_NAMES[month] || "";

  const selectedProjectName = React.useMemo(() => {
    if (projectId === "ALL") return null;
    if (projectId === "NO_PROJECT") return "General";
    if (Array.isArray(partyProjects)) {
      const found = partyProjects.find((p) => String(p?.id) === String(projectId));
      if (found?.name) return found.name;
    }
    return projectId;
  }, [projectId, partyProjects]);

  // Group calls by date for active dates (Only dates with calls)
  const activeDateGroups = React.useMemo(() => {
    if (!callsForMonth || !callsForMonth.length) return [];
    const map = {};

    callsForMonth.forEach((c) => {
      if (!c.Date) return;
      const d = new Date(c.Date);
      if (isNaN(d.getTime())) return;
      const dateNum = d.getDate();
      if (!map[dateNum]) {
        const dayOfWeekIndex = d.getDay();
        const dayOfWeekName = DAYS_OF_WEEK[dayOfWeekIndex]?.name || "";
        map[dateNum] = {
          dateNum,
          dayOfWeekName,
          dateObj: d,
          calls: [],
        };
      }
      map[dateNum].calls.push(c);
    });

    // Sort ascending by date
    return Object.values(map).sort((a, b) => a.dateNum - b.dateNum);
  }, [callsForMonth]);

  const nowFormatted = React.useMemo(() => {
    const d = new Date();
    return `${formatDate(d)} at ${formatTime(d)}`;
  }, []);

  const resolutionRate =
    monthStats.total > 0
      ? `${Math.round((monthStats.completed / monthStats.total) * 100)}%`
      : "100%";

  const is5Weeks = calendarWeeks.length <= 5;

  return (
    <div
      id="print-report-root"
      className={`print-report-container ${isScreenPreview ? "screen-preview-container" : ""}`}
    >
      {/* =========================================================================
          PAGE 1: COMPLETE CALENDAR & MONTHLY SUMMARY (Strictly 1 Page in Landscape)
         ========================================================================= */}
      <PartyCallMonthCalendarSheet
        party={party}
        month={month}
        year={year}
        selectedProjectName={selectedProjectName}
        calendarWeeks={calendarWeeks}
        monthStats={monthStats}
        pageNumber={1}
        totalPages={null}
        isLastPage={activeDateGroups.length === 0}
        isScreenPreview={isScreenPreview}
        nowFormatted={nowFormatted}
      />

      {/* =========================================================================
          PAGE 2+: DETAILED TASK INFORMATION (Grouped Date-wise for Active Days)
         ========================================================================= */}
      {activeDateGroups.length > 0 && (
        <div className={`print-details-container ${isScreenPreview ? "screen-preview-sheet" : ""}`}>
          {isScreenPreview && (
            <div className="screen-page-tag">Pages 2+: Detailed Task & Call Records</div>
          )}

          <div className="print-details-section-header">
            <div>
              <div className="print-details-section-title">
                DETAILED TASK & CALL INFORMATION
              </div>
              <div className="print-details-section-sub">
                Comprehensive records for all {callsForMonth.length} call(s) logged in {monthName} {year} &bull; {partyName}
              </div>
            </div>
            <div style={{ fontSize: "8.5px", color: "#64748b", fontWeight: 700 }}>
              {activeDateGroups.length} Active Day(s) with Calls
            </div>
          </div>

          {/* Iterate over each active date */}
          {activeDateGroups.map((group) => {
            const dateStr = `${group.dayOfWeekName}, ${group.dateNum} ${monthName} ${year}`;

            return (
              <div key={group.dateNum} className="print-date-group">
                {/* Date Header Banner */}
                <div className="print-date-banner">
                  <span className="print-date-title">📅 {dateStr}</span>
                  <span className="print-date-badge">
                    {group.calls.length} {group.calls.length === 1 ? "Call Record" : "Call Records"}
                  </span>
                </div>

                {/* All calls for this date */}
                {group.calls.map((c) => {
                  const badge = getStatusBadge(c.Status);
                  const duration = calculateDuration(c.Date, c.Complate_Date, c.Status);

                  const projectName =
                    c.Project?.project_name ||
                    c.Project?.ProjectName ||
                    (typeof c.Project === "string" ? c.Project : "-");

                  const moduleName =
                    c.Module?.ModuleName ||
                    c.Module?.module_name ||
                    (typeof c.Module === "string" ? c.Module : "-");

                  const typeName =
                    c.Type?.Type_name ||
                    c.Type?.TypeName ||
                    c.Type?.type_name ||
                    (typeof c.Type === "string" && c.Type !== "-" ? c.Type : null) ||
                    c.Requirement_Type ||
                    "-";

                  // Generator
                  const generatorObj = c.InPutId;
                  const generatorName =
                    generatorObj?.Name ||
                    generatorObj?.OwnerName ||
                    (typeof generatorObj === "string" ? generatorObj : null) ||
                    c.Employee ||
                    "Not Specified";
                  const generatorRole = generatorObj?.Role || null;

                  // Assigned Support
                  const supportObj = c.SuportTeamId;
                  const supportStaffName =
                    supportObj?.Name ||
                    supportObj?.OwnerName ||
                    (typeof supportObj === "string" ? supportObj : null);
                  const supportStaffRole = supportObj?.Role || null;

                  // Senior Dev
                  const seniorDevObj = c.SeniorDevloperID;
                  const seniorDevName =
                    seniorDevObj?.Name ||
                    seniorDevObj?.OwnerName ||
                    (typeof seniorDevObj === "string" ? seniorDevObj : null);
                  const seniorDevStatus = c.SeniorDevloperStatus || null;

                  // Contact
                  const contactName = c.ContactPerName || null;
                  const contactPhone = c.ContactPer || null;

                  // Records history
                  const recordLogs = Array.isArray(c.Record) ? c.Record : [];

                  // Descriptions
                  const presentStatusText = (c.PresentStatus || "").trim();
                  const requirementText = (c.Requirement || "").trim();
                  const solutionText = (c.Solution || "").trim();
                  const benefitText = (c.Benefit || "").trim();

                  const hasPresent = Boolean(presentStatusText && presentStatusText !== "-");
                  const hasReq = Boolean(requirementText && requirementText !== "-");
                  const hasSol = Boolean(
                    solutionText &&
                    solutionText !== "-" &&
                    solutionText.toLowerCase() !== "null"
                  );
                  const hasBen = Boolean(
                    benefitText &&
                    benefitText !== "-" &&
                    benefitText.toLowerCase() !== "null"
                  );

                  const areSame =
                    hasPresent &&
                    hasReq &&
                    presentStatusText.toLowerCase() === requirementText.toLowerCase();

                  // Extract strictly JPG and PNG photo attachments
                  const rawPhotos = Array.isArray(c?.Photos)
                    ? c.Photos
                    : Array.isArray(c?.photos)
                    ? c.photos
                    : Array.isArray(c?.attachments)
                    ? c.attachments
                    : [];

                  const callImages = rawPhotos
                    .map((p) => (typeof p === "string" ? p.trim() : p?.file || p?.url || ""))
                    .filter((url) => Boolean(url) && isJpgOrPng(url));

                  return (
                    <div key={c._id || c.CallNo} className="print-task-card">
                      {/* Card Header */}
                      <div className="print-task-header">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span className="print-task-callno">
                            CALL #{c.CallNo || "N/A"}
                          </span>
                          <span
                            className={`print-task-status-pill ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                          {duration && (
                            <span
                              style={{
                                fontSize: "8px",
                                background: "rgba(255, 255, 255, 0.2)",
                                padding: "1px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              Duration: {duration}
                            </span>
                          )}
                          {includeImages && callImages.length > 0 && (
                            <span
                              style={{
                                fontSize: "8px",
                                background: "rgba(255, 255, 255, 0.25)",
                                padding: "1px 6px",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                fontWeight: 700,
                              }}
                              title={`${callImages.length} attached photo(s)`}
                            >
                              📷 {callImages.length} {callImages.length === 1 ? "Photo" : "Photos"}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "8.5px", opacity: 0.9 }}>
                          Logged: {formatDate(c.Date)} at {formatTime(c.Date)}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="print-task-body">
                        {/* 4-Column Meta Grid */}
                        <div className="print-task-grid">
                          <div className="print-grid-item">
                            <span className="print-grid-label">Company Name</span>
                            <span className="print-grid-val">{partyName}</span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">Generated By</span>
                            <span className="print-grid-val">{generatorName}</span>
                            {generatorRole && (
                              <span className="print-grid-sub">({generatorRole})</span>
                            )}
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">Generated Time</span>
                            <span className="print-grid-val">
                              {formatDate(c.Date)} {formatTime(c.Date)}
                            </span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">
                              {c.Complate_Date ? "Completed On" : "Call Status"}
                            </span>
                            <span className="print-grid-val">
                              {c.Complate_Date
                                ? `${formatDate(c.Complate_Date)} ${formatTime(c.Complate_Date)}`
                                : c.Status || "Pending"}
                            </span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">Project</span>
                            <span className="print-grid-val">{projectName}</span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">Module</span>
                            <span className="print-grid-val">{moduleName}</span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">Type</span>
                            <span className="print-grid-val">{typeName}</span>
                          </div>

                          <div className="print-grid-item">
                            <span className="print-grid-label">
                              {supportStaffRole?.toLowerCase().includes("support") ? "Support Team" : "Employee"}
                            </span>
                            <span className="print-grid-val">
                              {supportStaffName || "Unassigned"}
                            </span>
                            {supportStaffRole && (
                              <span className="print-grid-sub">({supportStaffRole})</span>
                            )}
                          </div>
                        </div>

                        {/* Senior Developer if assigned */}
                        {seniorDevName && (
                          <div className="print-dev-strip">
                            <div>
                              <strong style={{ color: "#581c87" }}>
                                Assigned Senior Developer:
                              </strong>{" "}
                              <span style={{ fontWeight: 700 }}>{seniorDevName}</span>
                            </div>
                            {seniorDevStatus && (
                              <span
                                style={{
                                  fontSize: "8px",
                                  fontWeight: 700,
                                  background: "#f3e8ff",
                                  color: "#6b21a8",
                                  padding: "1px 5px",
                                  borderRadius: "3px",
                                  border: "1px solid #d8b4fe",
                                }}
                              >
                                Dev Status: {seniorDevStatus}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Contact Person info if available */}
                        {(contactName || contactPhone) && (
                          <div className="print-contact-strip">
                            <div>
                              <strong style={{ color: "#166534" }}>Contact Person:</strong>{" "}
                              <span>{contactName || "N/A"}</span>
                            </div>
                            {contactPhone && (
                              <div>
                                <strong style={{ color: "#166534" }}>Phone:</strong>{" "}
                                <span>{contactPhone}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Assignment & Forwarding History (if any) */}
                        {includeHistory && recordLogs.length > 0 && (
                          <div className="print-history-box">
                            <div className="print-history-title">
                              Assignment & Forwarding History ({recordLogs.length}{" "}
                              {recordLogs.length === 1 ? "Record" : "Records"})
                            </div>
                            {recordLogs.map((rec, rIdx) => {
                              const staff =
                                rec.InHouseId?.Name ||
                                rec.InHouseId?.OwnerName ||
                                (typeof rec.InHouseId === "string"
                                  ? rec.InHouseId
                                  : "Support Member");
                              const role = rec.InHouseId?.Role;
                              const logD = formatDate(rec.Date);
                              const logT = formatTime(rec.Date);

                              return (
                                <div
                                  key={rec._id || rIdx}
                                  className="print-history-item"
                                >
                                  <div className="print-step-badge">
                                    {rIdx + 1}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: "4px",
                                      }}
                                    >
                                      <span>
                                        <strong>Forwarded / Assigned To:</strong>{" "}
                                        {staff} {role && `(${role})`}
                                      </span>
                                      <span style={{ color: "#64748b" }}>
                                        {logD} at {logT}
                                      </span>
                                    </div>
                                    {rec.DescripTione && (
                                      <div
                                        style={{
                                          color: "#475569",
                                          marginTop: "1.5px",
                                          fontSize: "8px",
                                        }}
                                      >
                                        Remarks: {rec.DescripTione}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Requirement / Issue Description */}
                        <div className="print-desc-box">
                          <div className="print-desc-title">
                            Requirement / Issue Description
                          </div>
                          {hasPresent && hasReq && !areSame ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                              <div>
                                <span
                                  style={{
                                    fontSize: "7.5px",
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    display: "block",
                                  }}
                                >
                                  Present Status / Issue:
                                </span>
                                <div className="print-desc-content">
                                  {presentStatusText}
                                </div>
                              </div>
                              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "2px" }}>
                                <span
                                  style={{
                                    fontSize: "7.5px",
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    display: "block",
                                  }}
                                >
                                  Specific Requirement:
                                </span>
                                <div className="print-desc-content">
                                  {requirementText}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="print-desc-content">
                              {presentStatusText || requirementText || (
                                <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                                  No requirement details provided.
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Solution if available */}
                        {hasSol && (
                          <div className="print-solution-box">
                            <div className="print-solution-title">
                              ✓ Solution / Action Taken
                            </div>
                            <div className="print-solution-content">
                              {solutionText}
                            </div>
                          </div>
                        )}

                        {/* Benefit if available */}
                        {hasBen && (
                          <div
                            style={{
                              border: "1px solid #c7d2fe",
                              borderRadius: "5px",
                              padding: "5px 8px",
                              background: "#eef2ff",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "8px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                color: "#3730a3",
                                marginBottom: "1.5px",
                              }}
                            >
                              Benefit
                            </div>
                            <div
                              style={{
                                fontSize: "9px",
                                color: "#312e81",
                                lineHeight: "1.3",
                              }}
                            >
                              {benefitText}
                            </div>
                          </div>
                        )}

                        {/* Attached Photos (JPG / PNG only) - Half-Page Readable Display */}
                        {includeImages && callImages.length > 0 && (
                          <div className="print-attachments-box">
                            <div className="print-attachments-header">
                              <div className="print-attachments-title">
                                <FaImage style={{ fontSize: "9px", color: "#154D75" }} />
                                <span>
                                  Attached Photos &bull; JPG / PNG ({callImages.length}{" "}
                                  {callImages.length === 1 ? "Photo" : "Photos"})
                                </span>
                              </div>
                              <span className="print-attachments-hint">
                                Large High-Resolution View &bull; Click image to open original in new tab
                              </span>
                            </div>
                            <div className="print-attachments-grid">
                              {callImages.map((imgUrl, imgIdx) => (
                                <div key={imgIdx} className="print-attachment-item">
                                  <div className="print-attachment-img-container">
                                    <img
                                      src={imgUrl}
                                      alt={`Call #${c.CallNo || ""} Attachment ${imgIdx + 1}`}
                                      className="print-attachment-img"
                                      onClick={() => {
                                        if (typeof window !== "undefined") {
                                          window.open(imgUrl, "_blank");
                                        }
                                      }}
                                      title="Click to open full resolution photo in new tab"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  </div>
                                  <div className="print-attachment-footer">
                                    <span className="print-attachment-label">
                                      Photo {imgIdx + 1} of {callImages.length}
                                    </span>
                                    <span
                                      className="print-attachment-zoom-tip"
                                      onClick={() => {
                                        if (typeof window !== "undefined") {
                                          window.open(imgUrl, "_blank");
                                        }
                                      }}
                                    >
                                      🔍 Open Original
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Details Section Running Footer */}
          <div className="print-page-footer">
            <span>Dynamic Softlink &bull; Detailed Support Call Logs</span>
            <span>Period: {monthName} {year} &bull; End of Report</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartyCallPrintReport;
